import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowRight, Calendar as CalIcon, MapPin } from "lucide-react";
import { Reveal } from "@/components/effects/Reveal";
import { T } from "@/context/SiteContent";
import { TripSetupModal } from "./tripsetup/TripSetupModal";

/* ==================== Trip Setup section (Step 1 above the catalog) ====================
 * Thin façade that renders either the compact "Choose dates, times & delivery" CTA
 * card, or a locked-in trip summary chip. All heavy lifting (form state,
 * DayPicker, address checks, etc.) lives inside `tripsetup/TripSetupModal.jsx`
 * and its focused sub-components.
 *
 * StickyTripBar is re-exported here for backwards-compat with existing imports
 * from `@/components/booking/TripSetup`. Its source of truth is
 * `@/components/booking/StickyTripBar`.
 */
export { StickyTripBar } from "./StickyTripBar";

export const TripSetup = ({
  range, isOffProperty, zoneId, resort, address, addrCheck,
  deliverySlot, pickupSlot, disneyAvailability,
  differentPickup, pickupIsOffProperty, pickupZoneId, pickupResort, pickupAddress, pickupAddrCheck,
  zones, timeSlots, onSave, externalOpen, onExternalOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (externalOpen !== undefined) setOpen(externalOpen); }, [externalOpen]);
  const setOpenBoth = (v) => { setOpen(v); onExternalOpenChange?.(v); };

  const isSet = !!(
    range?.from && range?.to &&
    ((isOffProperty && zoneId && (resort || addrCheck?.delivers)) || (!isOffProperty && resort)) &&
    deliverySlot && pickupSlot &&
    (isOffProperty || disneyAvailability)
  );

  const destinationLabel = (() => {
    if (!isSet) return "";
    if (isOffProperty) {
      const z = zones.find((zz) => zz.id === zoneId);
      if (z?.requires_address) return address;
      return resort ? `${resort} · ${z?.area || ""}` : (z?.area || "");
    }
    return resort;
  })();
  const pickupDestinationLabel = (() => {
    if (!differentPickup) return "";
    if (pickupIsOffProperty) {
      const z = zones.find((zz) => zz.id === pickupZoneId);
      if (z?.requires_address) return pickupAddress;
      return pickupResort ? `${pickupResort} · ${z?.area || ""}` : (z?.area || "");
    }
    return pickupResort;
  })();
  const timeLabel = (slots, id) => (slots || []).find((s) => s.id === id)?.label || id;

  return (
    <section id="trip-setup" className="section relative">
      <div className="section-tint" />
      <div className="container max-w-4xl">
        <Reveal>
          <p className="eyebrow mb-3"><T k="tripsetup.section.eyebrow" fallback="Where & when" /></p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">
            {isSet
              ? <T k="tripsetup.section.headline_set" fallback="Your trip is set — pick your gear below." />
              : <T k="tripsetup.section.headline_unset" fallback="Start here — pick dates, times & delivery." />}
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          {isSet ? (
            <div
              className="mt-6 rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4 md:p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center"
              data-testid="trip-summary-card"
            >
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 flex items-center justify-center shrink-0">
                    <CalIcon className="w-3.5 h-3.5 text-[#FFD166]" />
                  </div>
                  <div className="min-w-0">
                    <div className="eyebrow text-[#FFD166] text-[9px]">Rental dates &amp; times</div>
                    <div className="text-[#F6F1E4] text-sm font-medium" data-testid="trip-summary-dates">
                      {format(range.from, "MMM d")} → {format(range.to, "MMM d")}
                    </div>
                    <div className="text-[10px] text-[#B9C8D5]">
                      {timeLabel(timeSlots, deliverySlot)} · pickup {timeLabel(timeSlots, pickupSlot)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 flex items-center justify-center shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-[#FFD166]" />
                  </div>
                  <div className="min-w-0">
                    <div className="eyebrow text-[#FFD166] text-[9px]">
                      {differentPickup ? "Delivery / Pickup" : "Delivery to"}
                    </div>
                    <div className="text-[#F6F1E4] text-sm font-medium truncate" data-testid="trip-summary-destination">{destinationLabel}</div>
                    {differentPickup ? (
                      <div className="text-[10px] text-[#B9C8D5] truncate" data-testid="trip-summary-pickup-destination">
                        Pickup: <span className="text-[#FFD166]">{pickupDestinationLabel}</span>
                      </div>
                    ) : (
                      <div className="text-[10px] text-[#B9C8D5]">
                        {isOffProperty ? (zones.find((z) => z.id === zoneId)?.area || "Off-site") : "Walt Disney World"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button type="button" onClick={() => setOpenBoth(true)} className="btn-ghost shrink-0 text-xs px-3 py-1.5" data-testid="trip-summary-edit">
                Change
              </button>
            </div>
          ) : (
            /* Compact CTA — small, punchy, single-line on desktop. */
            <button
              type="button"
              onClick={() => setOpenBoth(true)}
              className="mt-6 w-full rounded-xl border-2 border-dashed border-[#FFD166]/50 bg-[#FFD166]/[0.04] hover:border-[#FFD166] hover:bg-[#FFD166]/10 transition-colors px-5 py-4 flex items-center gap-4 text-left"
              data-testid="trip-setup-cta"
            >
              <div className="w-10 h-10 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 flex items-center justify-center shrink-0">
                <CalIcon className="w-4 h-4 text-[#FFD166]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg md:text-xl text-[#F6F1E4] leading-tight"><T k="tripsetup.cta.title" fallback="Choose dates, times & delivery" /></div>
                <div className="text-[11px] md:text-xs text-[#B9C8D5] mt-0.5 truncate"><T k="tripsetup.cta.blurb" fallback="Unlock the catalog with your trip details" /></div>
              </div>
              <div className="shrink-0 flex items-center gap-1 text-[#FFD166] text-sm font-medium">
                Start <ArrowRight className="w-4 h-4" />
              </div>
            </button>
          )}
        </Reveal>
      </div>

      <TripSetupModal
        open={open}
        onClose={() => setOpenBoth(false)}
        onConfirm={(data) => {
          onSave(data);
          setOpenBoth(false);
          toast.success("Trip locked in — let's find you some gear ✨");
          // Scroll to catalog so the user sees the inventory they came for.
          requestAnimationFrame(() => {
            const el = document.getElementById("catalog");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }}
        initialRange={range}
        initialIsOffProperty={isOffProperty}
        initialZoneId={zoneId}
        initialResort={resort}
        initialAddress={address}
        initialAddrCheck={addrCheck}
        initialDeliverySlot={deliverySlot}
        initialPickupSlot={pickupSlot}
        initialDisneyAvailability={disneyAvailability}
        initialDifferentPickup={differentPickup}
        initialPickupIsOffProperty={pickupIsOffProperty}
        initialPickupZoneId={pickupZoneId}
        initialPickupResort={pickupResort}
        initialPickupAddress={pickupAddress}
        initialPickupAddrCheck={pickupAddrCheck}
        zones={zones}
        timeSlots={timeSlots}
      />
    </section>
  );
};
